import { useContext } from 'react';

import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';
import { isMobileEmbedded } from '@rover/rsdk/src/modules/Network/userAgent';
import { ProviderStates } from '@rover/types/src/ProviderState';

import LocationContext from '../../../context/LocationContext';
import { providerProfile } from '../constants/routes';

import { useShouldShowNewManagementHub } from './useShouldShowNewManagementHub';

export const RETURN_PATH_QUERY_PARAM = 'return_to';

export const MANAGEMENT_HUB_PATH = '/management-hub/';

/**
 * The hub deeplink the apps follow to close a web view and reopen the native
 * hub on the section and tab the provider left.
 */
export const MANAGEMENT_HUB_DEEPLINK_PREFIX = 'rover://management-hub?';

/**
 * Leaves an embedded web view for the native hub, replacing the back stack the
 * web pages built rather than adding to it, so the hub is where "back" lands
 * next. Mirrors `management_hub.constants.MANAGEMENT_HUB_RELAUNCH_DEEPLINK`.
 */
export const MANAGEMENT_HUB_RELAUNCH_DEEPLINK = `${MANAGEMENT_HUB_DEEPLINK_PREFIX}force=true&navigation=popAndLaunch&rebuildBackstack=true`;

/**
 * Whether this page is running inside an app web view, as far as the browser can
 * tell. `isMobileEmbedded` reads `document`, so it answers `false` while
 * rendering on the server and the real answer once hydrated.
 */
const isEmbeddedWebView = (): boolean => typeof document !== 'undefined' && isMobileEmbedded();

const MANAGEMENT_HUB_RETURN_PREFIXES = [MANAGEMENT_HUB_PATH, MANAGEMENT_HUB_DEEPLINK_PREFIX];

const LEGACY_HUB_RETURN_PREFIXES = [providerProfile];

/**
 * The `return_to` in `search` where it names somewhere we return providers to,
 * and `undefined` where there is none to return to or carry along.
 *
 * `allowManagementHub` says which hub's paths count as somewhere we do: only the
 * hub a provider actually uses, so a stale or hand-edited `return_to` cannot
 * strand them on the other one. Both allow-lists hold our own paths only, so
 * this can never become an open redirect.
 */
const readHubReturnPath = (search: string, allowManagementHub: boolean): string | undefined => {
  const requestedReturnPath = new URLSearchParams(search).get(RETURN_PATH_QUERY_PARAM);
  const allowedPrefixes = allowManagementHub
    ? MANAGEMENT_HUB_RETURN_PREFIXES
    : LEGACY_HUB_RETURN_PREFIXES;

  return allowedPrefixes.some((prefix) => requestedReturnPath?.startsWith(prefix))
    ? (requestedReturnPath as string)
    : undefined;
};

/**
 * The `return_to` in `search`, or `fallbackPath` where it names somewhere we do
 * not return providers to.
 *
 * Callers outside a component reach for this directly; everyone else wants
 * `useHubReturnPath`, which reads the location and the rollout for them.
 */
export const resolveHubReturnPath = (
  search: string,
  { allowManagementHub, fallbackPath }: { allowManagementHub: boolean; fallbackPath: string }
): string => readHubReturnPath(search, allowManagementHub) ?? fallbackPath;

/**
 * Whether to return this provider to the management hub.
 *
 * True when the management hub is the hub they are served and they are no
 * longer signing up: a provider whose sign-up is not submitted yet belongs on
 * the legacy hub.
 *
 * Keep in sync with the `hub_return_path` Django helper.
 */
const useShouldReturnToManagementHub = (): boolean => {
  const shouldShowNewManagementHub = useShouldShowNewManagementHub();
  const { user } = useCurrentUser();

  return (
    shouldShowNewManagementHub &&
    user?.providerState !== ProviderStates.applicant_with_services_selected
  );
};

export const useHubReturnPathInUrl = (): string | undefined => {
  // Read the context directly rather than through `useLocationContext`, which
  // throws without a provider: the legacy bundles Django templates mount have
  // none, and there the browser's own location is the only one there is.
  const location = useContext(LocationContext);
  const search = location?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const shouldReturnToManagementHub = useShouldReturnToManagementHub();

  return readHubReturnPath(search, shouldReturnToManagementHub);
};

/**
 * Adds this page's return target to a URL it sends the provider on to, leaving
 * it alone when there is none.
 *
 * Flows that walk through several pages before the provider is done -- pets
 * reaches add and edit through the pet list -- have to hand the target along at
 * each step, or the page they finally leave from has nothing to return to and
 * drops the provider on a hub landing page instead of where they came from.
 */
export const useAppendHubReturnPath = (): ((url: string) => string) => {
  const returnPath = useHubReturnPathInUrl();

  return (url) => {
    if (!returnPath) return url;

    const [path, existingSearch] = url.split('?');
    const params = new URLSearchParams(existingSearch);
    params.set(RETURN_PATH_QUERY_PARAM, returnPath);

    return `${path}?${params.toString()}`;
  };
};

/**
 * Where a page opened from a provider hub should send the provider back to.
 *
 * Both hubs pass the destination as `return_to`, since only the hub knows which
 * section and tab the provider came from -- and, in the apps, whether returning
 * means following a deeplink that closes the web view. Which hub that may be is
 * decided here rather than trusted from the URL: a provider on the management
 * hub returns to the management hub, everyone else to the legacy hub, so a stale
 * or hand-edited `return_to` cannot strand a provider on the wrong one.
 *
 * A `return_to` that does not match the provider's own hub is discarded in
 * favour of that hub's landing page. Both allow-lists hold our own paths only,
 * so this can never become an open redirect.
 *
 * Pages that a sign-up flow also walks through pass `fallbackPath` so that a
 * provider who arrived without a `return_to` continues through sign-up instead
 * of being dropped on a hub they have not finished qualifying for.
 */
export const useHubReturnPath = (fallbackPath?: string): string => {
  const shouldReturnToManagementHub = useShouldReturnToManagementHub();
  const returnPathInUrl = useHubReturnPathInUrl();

  if (returnPathInUrl) return returnPathInUrl;
  if (fallbackPath) return fallbackPath;
  if (!shouldReturnToManagementHub) return providerProfile;

  // In the apps the management hub is a native screen the web view was opened
  // over, so returning to it means closing that web view. Loading the web hub
  // inside it instead stands a second hub on top of the native one and leaves
  // Back walking the web view's own history, alternating between the hub and
  // whichever page the provider opened from it. Mirrors the embedded branch of
  // `management_hub.return_paths.hub_return_path`.
  if (isEmbeddedWebView()) return MANAGEMENT_HUB_RELAUNCH_DEEPLINK;

  return MANAGEMENT_HUB_PATH;
};

export default useHubReturnPath;
